// Type-only: keeps this module free of any runtime edge back into the component it is
// imported BY, so the pure builders load in a plain node test environment.
import type { CustomerSwitchLeg } from "./CustomerSwitchModal";
// Pure util, no component edge — safe to import at runtime here. Reused rather than
// re-derived: parallel copies of this derivation are what produced the D1 defect.
import { ServiceCapabilities, deriveServiceCapabilities } from "../../../utils/serviceCapabilities";

// S5d — pure request builders for the Customer Switch popup.
//
// These live outside the component ON PURPOSE. The switchType stamped on each leg is
// the single highest-risk detail in this feature (plan §9a / OQ-7): the wizard's
// switchType effect SETS but never CLEARS, so a stale "Customer Switch" leaking into
// the second leg's contract would recreate exactly the mislabelling bug this whole
// effort exists to fix. Keeping payload construction pure makes that guarantee
// executable instead of a claim — see tests in customerSwitchPayloads.trace.
//
// Canonical FT-3581 values; both are already in the sdps_*.switchType vocabulary.
export const SWITCH_LEG_SWITCH_TYPE = "Customer Switch" as const;
export const SECOND_LEG_SWITCH_TYPE = "Supplier Switch" as const;

export type SwitchDirection = "injection" | "offtake";

/** The admin's per-direction pick of WHICH of B's active contracts to inherit from. */
export interface SourceSelection {
  injection?: number;
  offtake?: number;
}

// ---------------------------------------------------------------------------
// Backend contract (yuso-portals-backend src/types.ts). Field casing is
// load-bearing — the mixed camelCase money fields and snake_case partner_id /
// partner_contact_id / product_id are read verbatim and must NOT be normalised.
// ---------------------------------------------------------------------------

export type SwitchParamSource = "B" | "A" | "defaults";

export interface SwitchInjectionParams {
  fixedFee: number | null;
  minPlatformFee: number | null;
  imbCostPct: number | null;
  windImbCostPct: number | null;
  partner_id: number | null;
  partner_contact_id: number | null;
  partnerFee: number | null;
}

export interface SwitchOfftakeParams {
  balancingFee: number | null;
  minPlatformFee: number | null;
  priceGSC: number | null;
  priceWKC: number | null;
  product_id: number | null;
  partner_id: number | null;
  partner_contact_id: number | null;
  partnerFee: number | null;
}

export interface SwitchParamCandidate<P> {
  contractId: number;
  ean: string | null;
  startDate: string | null;
  /**
   * NULL (or the MySQL zero-date) means the contract was never signed. The backend keeps
   * unsigned rows in the list so the admin can see every option, and orders SIGNED FIRST
   * — its own comment is that unsigned fees were never agreed and must not seed a real
   * switched contract silently. The picker therefore has to SHOW this, which is the whole
   * point of the provenance display: an unsigned source may still be chosen, but only as
   * an informed choice.
   */
  signatureDate: string | null;
  companyId: number;
  companyName: string | null;
  params: P;
}

export interface SwitchParamProvenance {
  source: SwitchParamSource;
  companyId: number | null;
  companyName: string | null;
  ean: string | null;
  contractId: number | null;
  startDate: string | null;
}

/** The customer-switch shape of the contractOverview response. */
export interface SwitchOverviewResponse {
  customerSwitch?: boolean;
  switchDate?: string | null;
  resolvedParams?: {
    injection: SwitchInjectionParams | null;
    offtake: SwitchOfftakeParams | null;
  };
  provenance?: {
    injection: SwitchParamProvenance | null;
    offtake: SwitchParamProvenance | null;
  };
  /**
   * Populated for BOTH directions regardless of contract_type — the ask-phase needs
   * the other direction's candidates. Most-recent-first; `[]`, never null.
   */
  candidateSources?: {
    injection: SwitchParamCandidate<SwitchInjectionParams>[];
    offtake: SwitchParamCandidate<SwitchOfftakeParams>[];
  };
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Post-switch ownership rows (checkExistingEan)
// ---------------------------------------------------------------------------

/**
 * One row of the EAN's ownership view. The backend returns this from `CheckExistingEan`
 * — un-company-filtered, `latest = 1`, and `status <> 'ended'` — so a row exists for a
 * direction only when SOME company holds a live contract on it. An SDP with no contract
 * yields no row (the `latest = 1` predicate sits in the WHERE, not the JOIN).
 *
 * Only the fields this module reads are declared; the real row is much wider.
 */
export interface EanOwnershipRow {
  direction: string;
  company_id: number;
  installation_id?: string | null;
  activeContract?: string | null;
}

/**
 * The backend emits Dutch direction labels on these rows (SQL literals `'Injectie'` /
 * `'Afname'`). Compared case-insensitively like `resolveCustomerSwitchLegs` does, not with
 * the wizard's exact `=== "Injectie"`: a casing miss here would report a HELD direction as
 * free, which is the dangerous direction of failure.
 */
const DIRECTION_LABEL: Record<SwitchDirection, string> = {
  injection: "injectie",
  offtake: "afname",
};

const rowsForDirection = (
  rows: EanOwnershipRow[] | null | undefined,
  direction: SwitchDirection
): EanOwnershipRow[] =>
  (rows ?? []).filter((r) => (r.direction ?? "").trim().toLowerCase() === DIRECTION_LABEL[direction]);

/**
 * 🔴 F2 — is this direction genuinely FREE, i.e. held by NOBODY?
 *
 * The ask-phase may only offer a direction no company holds. `resolveCustomerSwitchLegs`
 * computes its `hasInj`/`hasOff` over FOREIGN rows only, so in the D5 shape (landing
 * company holds one direction, the outgoing company the other) it would happily offer a
 * direction the landing company ALREADY holds actively — and the backend then runs
 * `updateAtriasConfig` unconditionally, overwriting that live SDP's annualenergy and
 * stamping a switchType for a contract that was never created. Live data corruption on a
 * row that was never part of the switch.
 *
 * Keys on the POST-switch rows, never the stale pre-switch snapshot. `null` rows (the
 * best-effort re-read failed) means we cannot prove the direction is free, so we treat it
 * as taken and skip the ask-phase — the admin can still onboard it through the normal
 * wizard. Refusing on unknown state is the only safe default here.
 */
export const isDirectionFree = (
  rows: EanOwnershipRow[] | null | undefined,
  direction: SwitchDirection
): boolean => Array.isArray(rows) && rowsForDirection(rows, direction).length === 0;

/**
 * The `installation_id` / `activeContract` pair for one direction under ONE company,
 * derived exactly as the wizard derives it in `handleSubmitContractProposition`
 * (`checkExistingEan.find(direction === "Injectie")?.installation_id || null`) — only
 * scoped to the company, which the wizard's own `find()` is not.
 *
 * The wizard's localStorage `installationNumbers` is deliberately NOT consulted: that is
 * the outer wizard's state, and this popup owns its own data end-to-end.
 */
export const resolveDirectionInstallation = (args: {
  rows: EanOwnershipRow[] | null | undefined;
  direction: SwitchDirection;
  companyId: number;
}): { installation_id: string | null; activeContract: string | null } => {
  const own = rowsForDirection(args.rows, args.direction).find(
    (r) => Number(r.company_id) === Number(args.companyId)
  );
  return {
    installation_id: own?.installation_id ?? null,
    activeContract: own?.activeContract ?? null,
  };
};

/** Reads the post-commit `checkExistingEan` re-read out of an addContract response. */
export const readPostSwitchEanRows = (data: unknown): EanOwnershipRow[] | null => {
  const body = (data ?? {}) as { response?: { checkExistingEan?: unknown }; checkExistingEan?: unknown };
  const rows = body.response?.checkExistingEan ?? body.checkExistingEan;
  return Array.isArray(rows) ? (rows as EanOwnershipRow[]) : null;
};

// ---------------------------------------------------------------------------
// Direction helpers
// ---------------------------------------------------------------------------

/**
 * The direction Company A did NOT hold — the ask-phase CANDIDATE, before any check that
 * the direction is actually free OR that it even exists on the point. `null` when there is
 * nothing to offer: a `combined` switch leg already created both, and no leg means no
 * switch ran.
 *
 * Do NOT use this directly to drive the ask-phase — use `resolveAskPhaseDirection`.
 */
export const resolveSecondLegDirection = (legs: CustomerSwitchLeg[]): SwitchDirection | null => {
  if (legs.length !== 1) return null;
  const d = legs[0].direction;
  if (d === "injection") return "offtake";
  if (d === "offtake") return "injection";
  return null; // combined — both directions already handled in the switch txn
};

/**
 * The direction the ask-phase may actually offer is decided by `decideAskPhase` further
 * down — the one A did not hold, that no company holds after the switch (F2), AND that the
 * connection point actually has. When there is nothing to add the popup goes straight to
 * the completion screen — the reading consistent with §5a's "the other, NON-EXISTING
 * direction" and §5p's "B holds both ⇒ nothing to switch".
 */
/**
 * Backend codes whose message carries case-specific detail the localised copy cannot
 * express, and which the admin needs in order to act.
 *
 * CUSTOMER_SWITCH_CONTRACT_CHANGED is deliberately NOT here: one of its two backend
 * messages is "affected 0 rows instead of 1", which is internal diagnostics rather than
 * something an admin can act on. Its mapped copy already says the actionable part — the
 * connection point changed, run the lookup again.
 *
 * CUSTOMER_SWITCH_FLEX_SIBLING_EAN is raised at TWO backend sites describing DIFFERENT
 * companies' data with DIFFERENT remedies: one is Company A's flex site also linking a
 * connection point outside this switch (investigate A — the flex probably has to be split
 * first) and it names the direction AND the offending installation id; the other is the
 * LANDING company's site already linking a different point (investigate B — we refuse to
 * overwrite it). Showing only the mapped sentence discards that and leaves the admin
 * unable to tell which company to look at.
 */
/**
 * The backend error vocabulary MOVED to `src/utils/contractErrorCodes.ts`.
 *
 * It grew a second consumer — `SignCommodityContractPage`, a PUBLIC token-sign surface —
 * and a public page must not import from an add-service module. Re-exported here so every
 * existing import path keeps working, including the completeness test that pins the map
 * against the backend's code list.
 */
export {
  CUSTOMER_SWITCH_ERROR_KEYS,
  CODES_WITH_SERVER_DETAIL,
  shouldShowServerDetail,
  resolveContractErrorCopy,
} from "../../../utils/contractErrorCodes";
export type { ContractErrorBody } from "../../../utils/contractErrorCodes";

/**
 * Does the connection point HAVE this direction at all?
 *
 * 🔴 NOT `supportsIndependentDirections`, which requires BOTH directions and would
 * therefore reject a point that supports exactly the one being offered. The question here
 * is per-direction.
 */
export const isDirectionOnPoint = (caps: ServiceCapabilities, direction: SwitchDirection): boolean =>
  direction === "injection" ? caps.supportsInjection : caps.supportsOfftake;

/**
 * We know NOTHING about the point's directions. `deriveServiceCapabilities` returns
 * all-false both for an empty/absent config and for one carrying only unrecognised SC_
 * codes, and a real SDP always has at least one direction — so all-false means the input
 * told us nothing, not that the point has no directions. Read as UNKNOWN, and therefore
 * surfaced to the admin rather than silently skipped.
 */
export const areDirectionCapabilitiesUnknown = (caps: ServiceCapabilities): boolean =>
  !caps.supportsInjection && !caps.supportsOfftake;

/**
 * Why the ask-phase is offering nothing. Every skip has exactly ONE reason, and the two
 * "we did not know" reasons are what the askUndetermined banner keys off.
 */
export type AskPhaseSkipReason =
  | "noCandidate" // combined switch, or no leg ran — nothing was ever a candidate
  | "ownershipUnknown" // the post-commit re-read failed; cannot prove the direction is free
  | "directionTaken" // some company holds it — definitive, capabilities are irrelevant
  | "capabilitiesUnknown" // no usable service components; cannot prove the direction exists
  | "combinedOnlyPoint" // SC_OFFINJE only ⇒ a lone direction can never be contracted
  | "directionNotOnPoint"; // the point simply does not have this direction

export interface AskPhaseDecision {
  /** The direction to offer, or null when there is nothing to offer. */
  offer: SwitchDirection | null;
  /** Null exactly when `offer` is non-null. */
  reason: AskPhaseSkipReason | null;
}

/**
 * 🔴 THE ask-phase decision — ONE evaluation, so the live render and the post-run phase
 * choice cannot disagree.
 *
 * A direction is offerable only when ALL of these hold:
 *   1. it is the mirror of the single switch leg (`resolveSecondLegDirection`),
 *   2. NO company holds it after the handover (`isDirectionFree` — ownership, F2), AND
 *   3. it actually EXISTS on the connection point, per the point's Atrias service
 *      components (`deriveServiceCapabilities`), the point is not SC_OFFINJE-only, AND a
 *      STANDALONE service config exists for it (`findDirectionServiceConfig`).
 *
 * (3) is the fix for the live defect on an INJECTION-ONLY point: ownership alone reads a
 * direction that does not exist as "free", because no ownership row can exist for it. The
 * ask-phase then offered offtake, `runSecondLeg` went straight to contractOverview (it
 * never calls addSdps — it only hardcodes `addEan: "complete"`), no `sdps_offtake` row was
 * ever created, and the backend's `getCertificatePrices` dereferenced `dgo[0].dgo` on an
 * empty result set → 500. Free and existing are different questions; both must be asked.
 *
 * Ownership is checked BEFORE capabilities on purpose: an occupied direction is a
 * definitive "nothing to offer" whatever the service components say, so it must not be
 * reported as an unknown.
 */
export const decideAskPhase = (
  legs: CustomerSwitchLeg[],
  postSwitchRows: EanOwnershipRow[] | null | undefined,
  sdpAtriasConfig?: string[]
): AskPhaseDecision => {
  const candidate = resolveSecondLegDirection(legs);
  if (!candidate) return { offer: null, reason: "noCandidate" };
  // Rows we could not read prove nothing — same refusal as an occupied direction, but a
  // DIFFERENT reason, because this one is a blind skip the admin has to be told about.
  if (!Array.isArray(postSwitchRows)) return { offer: null, reason: "ownershipUnknown" };
  if (!isDirectionFree(postSwitchRows, candidate)) return { offer: null, reason: "directionTaken" };

  const caps = deriveServiceCapabilities(sdpAtriasConfig);
  if (areDirectionCapabilitiesUnknown(caps)) return { offer: null, reason: "capabilitiesUnknown" };
  if (caps.onlyOffinje) return { offer: null, reason: "combinedOnlyPoint" };
  if (!isDirectionOnPoint(caps, candidate)) return { offer: null, reason: "directionNotOnPoint" };
  /**
   * 🔴 The capability flags are NOT sufficient on their own. `SC_OFFINJE` sets BOTH of
   * them, and `onlyOffinje` is true only when the deduped code list is EXACTLY
   * `["SC_OFFINJE"]` — so a config that MIXES SC_OFFINJE with one standalone code, e.g.
   * `["SC_OFFINJE,301MN","SC_COMMOFF,N01MN"]`, left `onlyOffinje` false while SC_OFFINJE
   * still reported the ABSENT direction as present. The gate offered injection; the very
   * next step, `findDirectionServiceConfig`, deliberately excludes SC_OFFINJE and returned
   * `""`. The second leg would then go out with an empty `serviceConfig`, `runSecondLeg`
   * never calls addSdps, no `sdps_*` row is created, and the second contractOverview hits
   * the zero-row path — the original incident, one config shape removed.
   *
   * So eligibility is asked of THE SAME SOURCE that produces the `serviceConfig`: a
   * direction is genuinely offerable only when a STANDALONE config exists for it. The gate
   * and the next step now answer from one place and cannot contradict each other, and the
   * empty-`serviceConfig` payload becomes unreachable as a consequence rather than needing
   * its own patch. Same reason code as above — for the admin it is the same refusal: the
   * point does not have this direction as something contractable on its own.
   */
  if (findDirectionServiceConfig(sdpAtriasConfig, candidate) === "") {
    return { offer: null, reason: "directionNotOnPoint" };
  }

  return { offer: candidate, reason: null };
};

/**
 * The ask-phase was skipped WITHOUT knowing whether the other direction could be offered.
 *
 * Two distinct blind spots collapse into the same "offer nothing" as a legitimate refusal:
 * the post-switch ownership rows could not be read, or the point's service components are
 * unknown. An admin testing a partial case must be able to tell those apart from "nothing
 * to offer, correct", so the completion screen keys off this instead of implying the
 * handover is complete.
 *
 * A KNOWN refusal (direction taken, combined-only point, direction not on the point) is
 * deliberately NOT undetermined — that answer is definitive.
 */
export const isAskPhaseUndetermined = (
  legs: CustomerSwitchLeg[],
  postSwitchRows: EanOwnershipRow[] | null | undefined,
  sdpAtriasConfig?: string[]
): boolean => {
  const { reason } = decideAskPhase(legs, postSwitchRows, sdpAtriasConfig);
  return reason === "ownershipUnknown" || reason === "capabilitiesUnknown";
};

export const resolveAskPhaseDirection = (
  legs: CustomerSwitchLeg[],
  postSwitchRows: EanOwnershipRow[] | null | undefined,
  sdpAtriasConfig?: string[]
): SwitchDirection | null => decideAskPhase(legs, postSwitchRows, sdpAtriasConfig).offer;

/**
 * Mirrors the backend's `isSignedCandidate` byte-for-byte — MySQL hands back `0000-00-00`
 * for an unset date column, which is NOT null but is still "never signed".
 */
export const isCandidateSigned = <P>(candidate: SwitchParamCandidate<P>): boolean =>
  candidate.signatureDate != null && candidate.signatureDate !== "0000-00-00";

/**
 * Default source pick per §4c.
 *
 * 🔴 THIS RULE MIRRORS THE BACKEND'S `chooseCandidate` EXACTLY —
 * `candidates.find(isSignedCandidate) ?? candidates[0]` (customerSwitch.ts) — and MUST be
 * changed in lockstep with it. Do NOT "simplify" it back to `candidates[0]`.
 *
 * `[0]` alone happens to agree today only because the query's ORDER BY sorts signed-first.
 * The server's rule does not depend on that ordering, so if anyone ever changes the SQL
 * sort, `[0]` would make the FE DISPLAY one set of money while the server WRITES another,
 * silently. Re-applying the preference here makes the two sides agree by construction.
 *
 * This is safe rather than a re-implementation: `isCandidateSigned` above is term-for-term
 * identical to the backend predicate, zero-date handling included, so there is no second
 * definition of "signed" to drift.
 */
export const pickDefaultCandidateId = <P>(candidates: SwitchParamCandidate<P>[] | undefined): number | undefined => {
  if (!candidates || candidates.length === 0) return undefined;
  return (candidates.find(isCandidateSigned) ?? candidates[0]).contractId;
};

/** Only offer the picker when there is a genuine choice to make. */
export const needsSourcePicker = <P>(candidates: SwitchParamCandidate<P>[] | undefined): boolean =>
  !!candidates && candidates.length > 1;

/**
 * Deterministic display order for the provenance panel, matching the INSERT column
 * lists in the backend (§4b). Kept as explicit arrays rather than Object.keys so the
 * panel never reorders on a response-shape change.
 */
export const INJECTION_PARAM_FIELDS: (keyof SwitchInjectionParams)[] = [
  "fixedFee",
  "minPlatformFee",
  "imbCostPct",
  "windImbCostPct",
  "partner_id",
  "partner_contact_id",
  "partnerFee",
];

export const OFFTAKE_PARAM_FIELDS: (keyof SwitchOfftakeParams)[] = [
  "balancingFee",
  "minPlatformFee",
  "priceGSC",
  "priceWKC",
  "product_id",
  "partner_id",
  "partner_contact_id",
  "partnerFee",
];

// ---------------------------------------------------------------------------
// Leg 1 — the Customer Switch itself (BE-authoritative params)
// ---------------------------------------------------------------------------

export const buildSwitchLegOverviewPayload = (args: {
  leg: CustomerSwitchLeg;
  companyId: number;
  ean: string;
  sourceSelection?: SourceSelection;
}): Record<string, unknown> => {
  const { leg, companyId, ean, sourceSelection } = args;
  const payload: Record<string, unknown> = {
    customerSwitch: true,
    switchType: SWITCH_LEG_SWITCH_TYPE,
    contract_type: leg.direction,
    company_id: companyId,
    ean,
  };
  if (leg.direction !== "offtake") {
    payload.injection = { serviceConfig: leg.injectionServiceConfig || "" };
  }
  if (leg.direction !== "injection") {
    payload.offtake = { serviceConfig: leg.offtakeServiceConfig || "" };
  }
  // Display-driven hint only: the server re-resolves the money fields itself and falls
  // back to its default pick when an id is not one of B's own active contracts.
  if (sourceSelection && (sourceSelection.injection !== undefined || sourceSelection.offtake !== undefined)) {
    payload.sourceSelection = { ...sourceSelection };
  }
  return payload;
};

/**
 * ONE ADDITIONAL connection point travelling with the primary in the same
 * transaction. The wire format is an ARRAY of these, and the PRIMARY IS NEVER IN IT.
 *
 * 🔴 Why the primary is excluded: its service configs already have exactly one home,
 * the top-level `injection`/`offtake` objects. Repeating them here would create two
 * sources of truth for the same string, and `updateAtriasConfig` splits that string
 * into `service_component`/`service_config` with NO validation — so a desync writes
 * the wrong ATRIAS config silently. Unrepresentable beats detected.
 *
 * 🔴 No per-member `contract_type`: the backend derives each member's directions
 * from `getForeignActiveSdps(member.ean, destinationCompanyId)` — the same predicate
 * mirrored client-side by `resolveSwitchLegs`. Sending our copy back would make a
 * direction mismatch merely *checked*; deriving it makes one unrepresentable.
 */
export interface SwitchGroupMember {
  ean: string;
  injectionServiceConfig: string | null;
  offtakeServiceConfig: string | null;
}

export const buildSwitchLegContractPayload = (
  overview: SwitchOverviewResponse,
  sourceSelection?: SourceSelection,
  group?: SwitchGroupMember[]
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    ...overview,
    switchType: SWITCH_LEG_SWITCH_TYPE,
    customerSwitch: true,
  };
  if (sourceSelection && (sourceSelection.injection !== undefined || sourceSelection.offtake !== undefined)) {
    payload.sourceSelection = { ...sourceSelection };
  }
  // Written AFTER the spread so an echoed key from the overview response (which is
  // typed with an index signature) can never win. Omitted entirely — not sent as an
  // empty array — for a single-point switch, so that request stays byte-identical to
  // what the wizard has always sent.
  if (group && group.length > 0) {
    payload.group = group.map((m) => ({
      ean: m.ean,
      injectionServiceConfig: m.injectionServiceConfig,
      offtakeServiceConfig: m.offtakeServiceConfig,
    }));
  }
  return payload;
};

/**
 * One entry of the `groupMembers` write receipt.
 *
 * 🔴 THE AUTHORITY FOR THESE NAMES IS `yuso-portals-backend/src/types.ts`,
 * `SwitchGroupMemberResult` — diff against that source, do not infer them. The keys
 * are `injection` / `offtake`, matching the TOP-LEVEL `injection` / `offtake` fields
 * of the same response, NOT the `*_installation_id` column names and NOT a
 * `...InstallationId` camelCase form.
 *
 * This is a silent-failure seam: the response is parsed out of a
 * `Record<string, unknown>`, so a wrong key here type-checks cleanly, coerces to `""`,
 * and makes `membersMissingContracts` report EVERY member as unwritten — firing the
 * fail-loud path on every successful group transfer.
 */
export interface GroupMemberResult {
  ean: string;
  /** B's new injection installation id, or `""` when that direction was not written. */
  injection: string;
  /** B's new offtake installation id, or `""` when that direction was not written. */
  offtake: string;
}

/**
 * The per-member write receipt out of an addContract response.
 *
 * Returns `null` when the key is ABSENT — which the caller MUST treat as a hard
 * failure whenever a group was sent, never as "no members". `missingWrittenDirections`
 * only ever inspects the top-level `injection`/`offtake`, i.e. the PRIMARY point, so
 * silently falling back to it would leave a sibling whose contract was never written
 * reported as a full success.
 */
export const readGroupMembers = (data: unknown): GroupMemberResult[] | null => {
  const body = (data ?? {}) as { response?: Record<string, unknown> } & Record<string, unknown>;
  const inner = (body.response ?? body) as Record<string, unknown>;
  const rows = inner.groupMembers;
  if (!Array.isArray(rows)) return null;
  return rows.map((row) => {
    const r = (row ?? {}) as Record<string, unknown>;
    const str = (v: unknown): string =>
      typeof v === "string" ? v : v === null || v === undefined ? "" : String(v);
    return {
      ean: str(r.ean),
      injection: str(r.injection),
      offtake: str(r.offtake),
    };
  });
};

/**
 * Every member EAN that came back with NO installation id on a direction it was
 * supposed to get, i.e. no contract row exists for it.
 *
 * A 200 is not proof: `updateContract*` returns `installation_id ?? ""` even when its
 * UPDATE matched zero rows, and the handler only treats the literal `"error"` as a
 * failure. So the body has to be read — for members exactly as it already is for the
 * primary.
 */
export const membersMissingContracts = (
  members: GroupMemberResult[],
  expected: Map<string, SwitchDirection[]>
): string[] =>
  members
    .filter((m) => {
      const directions = expected.get(m.ean) ?? [];
      return directions.some((d) => (d === "injection" ? m.injection === "" : m.offtake === ""));
    })
    .map((m) => m.ean);

/** True when the returned member EAN set is exactly the set that was sent. */
export const groupMemberSetMatches = (
  sent: SwitchGroupMember[],
  returned: GroupMemberResult[]
): boolean => {
  const sentKeys = new Set(sent.map((m) => m.ean));
  const returnedKeys = new Set(returned.map((m) => m.ean));
  // The response INCLUDES the primary, so it is a superset by exactly one entry.
  for (const key of sentKeys) if (!returnedKeys.has(key)) return false;
  return returnedKeys.size === sentKeys.size + 1;
};

// ---------------------------------------------------------------------------
// Leg 2 — the optional second direction. A PLAIN ONBOARDING, not a switch.
// ---------------------------------------------------------------------------

/**
 * 🔴 The second leg must NEVER carry `customerSwitch` and must NEVER carry
 * "Customer Switch". Company A never held this direction, so there is nothing to hand
 * over: touching A's data or entering performCustomerSwitch would be a bug. Per OQ-7b
 * the label is "Supplier Switch".
 *
 * Note there is no FE opt-in for customer-switch mode: the backend detects it from
 * `findForeignActiveSdps` (foreign rows + admin + single owner). By the time this runs,
 * leg 1 has ended A's rows, so the server takes the normal path on its own. Omitting
 * the flag is belt-and-braces, and the omission is asserted by the trace tests.
 */
export interface SecondLegFormValues {
  startDate: string;
  endDate: string;
  isFlexible: boolean;
  annualVolume: number | null;
  annualVolumeUnknown: boolean;
  partnerId: number | null;
  /** Offtake only — the FlexiGreen (1) / LocalGreen (2) select. */
  product?: string;
}

export const buildSecondLegOverviewPayload = (args: {
  direction: SwitchDirection;
  companyId: number;
  ean: string;
  serviceConfig: string;
  form: SecondLegFormValues;
  /** Injection only — carried straight from the preswitch, as the wizard does. */
  technologies?: string;
}): Record<string, unknown> => {
  const { direction, companyId, ean, serviceConfig, form, technologies } = args;
  const base: Record<string, unknown> = {
    switchType: SECOND_LEG_SWITCH_TYPE,
    contract_type: direction,
    company_id: companyId,
    ean,
    // Mirrors the wizard's own `|| 1` default (AddService handleFormSubmit).
    partner_id: form.partnerId || 1,
  };
  if (direction === "injection") {
    base.injection = {
      start_date: form.startDate,
      end_date: form.isFlexible ? null : form.endDate,
      is_flexible: form.isFlexible,
      annual_injection_volume: form.annualVolumeUnknown ? null : form.annualVolume,
      serviceConfig: serviceConfig || "",
      technologies: technologies || "",
    };
  } else {
    base.offtake = {
      start_date: form.startDate,
      end_date: form.isFlexible ? null : form.endDate,
      is_flexible: form.isFlexible,
      product_id: Number(form.product),
      annual_offtake_volume: form.annualVolumeUnknown ? null : form.annualVolume,
      serviceConfig: serviceConfig || "",
    };
  }
  return base;
};

/**
 * The service-component config for a single direction, using the same lookup the wizard
 * performs in handleFormSubmit (SC_INJECTI or SC_COMMINJ / SC_OFFTAKE or SC_COMMOFF).
 * SC_OFFINJE is deliberately NOT considered: a direction backed only by SC_OFFINJE cannot be
 * contracted on its own. That exclusion is now also THE ask-phase existence rule —
 * `decideAskPhase` calls this function and refuses ("directionNotOnPoint") when it returns
 * `""`, so the gate cannot offer a direction this lookup would fail to configure. Do not
 * "helpfully" make SC_OFFINJE match here: it would silently re-open that gate.
 */
export const findDirectionServiceConfig = (
  sdpAtriasConfig: string[] | undefined,
  direction: SwitchDirection
): string => {
  const cfgs = sdpAtriasConfig ?? [];
  const found =
    direction === "injection"
      ? cfgs.find((c) => c.includes("SC_INJECTI") || c.includes("SC_COMMINJ"))
      : cfgs.find((c) => c.includes("SC_OFFTAKE") || c.includes("SC_COMMOFF"));
  return found || "";
};

/**
 * 🔴 F1 — the second leg MUST carry the direction's `installation_id` / `activeContract`,
 * exactly as the wizard's own submit path does (`handleSubmitContractProposition`).
 *
 * The backend selects its INSERT branch on `contract.<direction>.installation_id === null`
 * — STRICT equality. A bare spread of the overview response omits the field entirely, so
 * it is `undefined`, `undefined === null` is false, and the gate falls through to the
 * `installation_id !== null` arm → `updateContract*` → `UPDATE … WHERE installation_id =
 * NULL` → ZERO rows matched. That function returns `installation_id ?? ""`, and the
 * handler's error guard only trips on the literal string `"error"`, so an empty string
 * comes back as HTTP 200. Net effect: no contract row is created, in EITHER direction, and
 * the modal reports success and generates a proposal PDF for a contract that does not
 * exist.
 *
 * Setting the pair explicitly makes the INSERT branch genuinely selected. Assert it with
 * `selectsInsertBranch` before sending.
 */
export const buildSecondLegContractPayload = (
  overview: SwitchOverviewResponse,
  args: {
    direction: SwitchDirection;
    companyId: number;
    /** The post-commit `checkExistingEan` re-read — never the pre-switch snapshot. */
    postSwitchRows: EanOwnershipRow[] | null | undefined;
  }
): Record<string, unknown> => {
  // Strip anything switch-flavoured that a response might carry back, then stamp the
  // plain-onboarding label. Order matters: the explicit keys must win over the spread.
  const { customerSwitch: _cs, sourceSelection: _ss, ...rest } = overview as SwitchOverviewResponse & {
    sourceSelection?: unknown;
  };
  void _cs;
  void _ss;

  const { installation_id, activeContract } = resolveDirectionInstallation({
    rows: args.postSwitchRows,
    direction: args.direction,
    companyId: args.companyId,
  });

  // Only the leg being created is touched. The overview echoes the other direction's key
  // back as a fee-only object, but addContract never reads it for a single-direction
  // contract_type, so it is left exactly as the server sent it.
  const existingLeg = (rest as Record<string, unknown>)[args.direction];
  return {
    ...rest,
    [args.direction]: {
      ...((existingLeg as Record<string, unknown> | undefined) ?? {}),
      installation_id,
      activeContract,
    },
    switchType: SECOND_LEG_SWITCH_TYPE,
  };
};

/**
 * A LOCAL PRE-FLIGHT REFUSAL — not a live mirror of the backend.
 *
 * 🔴 Do not read this as "the backend will INSERT". It was modelled on the shape of
 * `contracts.ts::addContract`'s first gate (`installation_id === null || activeContract
 * === "ended"`), but that is not a contract:
 *   - even inside that gate the backend re-checks `checkExistingContract` and can still
 *     route to an UPDATE, so entering it never guaranteed an INSERT; and
 *   - the backend's contract-lifecycle work replaces that gate outright, so the mirror
 *     will stop matching regardless.
 *
 * What it is FOR is unchanged and still correct: the ask-phase only ever offers a FREE
 * direction, so a second-leg payload that does not look like a fresh write is a payload
 * the browser should refuse to send. The check runs entirely BEFORE the request, so its
 * failure mode is one-directional — it can only false-NEGATIVE (block a legitimate
 * second leg, surfaced as ContractNotCreatedError), never wave a bad write through.
 * The backend remains the authority on what actually happens to the row.
 *
 * An ABSENT `installation_id` fails — that is the F1 defect, and `undefined` must never
 * be mistaken for `null` here. That part IS load-bearing; keep it.
 */
export const selectsInsertBranch = (
  payload: Record<string, unknown>,
  direction: SwitchDirection
): boolean => {
  const leg = payload[direction] as Record<string, unknown> | undefined;
  if (!leg || !Object.prototype.hasOwnProperty.call(leg, "installation_id")) return false;
  return leg.installation_id === null || leg.activeContract === "ended";
};

/** The directions a given contract_type actually writes a contract row for. */
export const directionsWrittenBy = (
  contractType: "injection" | "offtake" | "combined"
): SwitchDirection[] => (contractType === "combined" ? ["injection", "offtake"] : [contractType]);

/**
 * The `installation_id` addContract reports back for one direction, `""` when the write
 * matched nothing. A 200 is NOT proof of success: `updateContract*` returns
 * `installation_id ?? ""` even when its UPDATE matched zero rows, and the handler only
 * treats the literal `"error"` as a failure. So the body must be read.
 */
export const readAddContractInstallationId = (data: unknown, direction: SwitchDirection): string => {
  const body = (data ?? {}) as { response?: Record<string, unknown> } & Record<string, unknown>;
  const inner = (body.response ?? body) as Record<string, unknown>;
  const value = inner[direction];
  if (typeof value === "string") return value;
  return value === null || value === undefined ? "" : String(value);
};

/**
 * The directions that were asked for but came back with no installation_id — i.e. no
 * contract row exists. Empty array means every requested direction was written.
 */
export const missingWrittenDirections = (
  data: unknown,
  contractType: "injection" | "offtake" | "combined"
): SwitchDirection[] =>
  directionsWrittenBy(contractType).filter((d) => readAddContractInstallationId(data, d) === "");

/**
 * Guard used by the trace tests AND at runtime before the second leg is sent: a payload
 * that would mislabel the plain onboarding must never leave the browser.
 */
export const isSecondLegPayloadSafe = (payload: Record<string, unknown>): boolean =>
  payload.switchType === SECOND_LEG_SWITCH_TYPE &&
  payload.customerSwitch === undefined &&
  payload.sourceSelection === undefined;
